'use client';

import React from 'react';
import { serializeJsonLd } from '@/lib/structuredData';

const JsonLd = ({ data }) => {
    if (!data) return null;

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
                __html: serializeJsonLd(data)
            }}
        />
    );
};

export const BreadcrumbJsonLd = ({ items }) => {
    const data = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items.map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": item.url
        }))
    };

    return <JsonLd data={data} />;
};

export const TouristAttractionJsonLd = ({ poi, poiPage, lang, url, image, googlePlaceId }) => {
    if (!poi) return null;
    const countryName = lang === 'ru'
        ? (poi.country?.nameRu || poi.country?.name || poi.country?.nameEn)
        : (poi.country?.nameEn || poi.country?.name || poi.country?.nameRu);
    const countryCode = typeof poi.country?.iso2 === 'string'
        ? poi.country.iso2.toUpperCase()
        : '';

    const safeDescription = (() => {
        if (poiPage?.subtitle) return poiPage.subtitle;
        const blocks = poiPage?.intro;
        if (!Array.isArray(blocks)) return undefined;
        try {
            const text = blocks
                .map(block => (Array.isArray(block?.children) ? block.children.map(child => child?.text || '').join('') : ''))
                .join(' ')
                .trim();
            return text ? text.substring(0, 160) : undefined;
        } catch {
            return undefined;
        }
    })();

    const data = {
        "@context": "https://schema.org",
        "@type": "TouristAttraction",
        "name": poi.nameEn,
        "description": safeDescription,
        "inLanguage": lang === 'ru' ? 'ru-RU' : 'en-US'
    };

    if (url) {
        data.url = url;
    }

    if (poi.localName) {
        data.alternateName = poi.localName;
    }

    if (poi.coords) {
        data.geo = {
            "@type": "GeoCoordinates",
            "latitude": poi.coords.lat,
            "longitude": poi.coords.lng
        };
    }

    // sameAs: официальный сайт + Google Maps по place_id (entity-reconciliation / AI grounding)
    const sameAs = [];
    if (poi.officialSite) sameAs.push(poi.officialSite);
    if (googlePlaceId) sameAs.push(`https://www.google.com/maps/place/?q=place_id:${googlePlaceId}`);
    if (sameAs.length) data.sameAs = sameAs;

    // image — готовый абсолютный URL из родителя (poi.cover.image — неразвёрнутый ref,
    // .asset.url был всегда undefined).
    if (image) {
        data.image = image;
    }

    // Города хранят имя в name/localizedTitle, а title обычно пуст — гейт по
    // city.title был всегда ложным, и ВСЕ POI отдавали TouristAttraction без
    // PostalAddress (аудит 2026-08-10, §4.6). Полнота сущности важна для AI-ретривала.
    const cityLabel = poi.city?.localizedTitle || poi.city?.name || poi.city?.title;
    if (cityLabel) {
        data.address = {
            "@type": "PostalAddress",
            "addressLocality": cityLabel,
            ...(countryCode || countryName ? { "addressCountry": countryCode || countryName } : {})
        };
    }

    return <JsonLd data={data} />;
};

export default JsonLd;
